
/* Implements Warm Academic theme styling for navigation sidebar */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  LayoutDashboard, GraduationCap, BookOpen, LogOut, HeartPulse, 
  Settings, Users, FileQuestion, BarChart3, Shield, ShieldCheck, Stethoscope, Sparkles, Terminal, X,
  Megaphone, Archive, BookCheck, Layers, FileEdit, Timer, UserCircle, Zap, Beaker, HelpCircle,
  Podcast as PodcastIcon, Mic2, History, Mic, Activity, Award, Globe, Search, Bell,
  Tag, Database, CreditCard, MessageSquare, LineChart, Compass, Sun, Moon
} from 'lucide-react';
import { useAuth } from './AuthBridge';
import { AdUnit } from './AdUnit';
import { SponsoredService } from '../services/sponsoredService';
import { AdminPermission } from '../types';
import { useThemeTokens } from '../hooks/useActiveTheme';

interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  onLogout: () => void;
  isOpen: boolean;
  onClose: () => void;
  adminMode?: boolean; 
}

export const Sidebar: React.FC<SidebarProps> = ({ currentPage, onNavigate, onLogout, isOpen, onClose, adminMode = true }) => {
  const { user, activeCert } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isFree = user?.planTier === 'free';
  const isElite = user?.planTier === 'paid';
  const [config, setConfig] = useState(SponsoredService.getConfig());
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    try {
      return document.documentElement.classList.contains('dark') || localStorage.getItem('theme_preference') === 'dark';
    } catch {
      return false;
    }
  });

  const toggleTheme = () => {
    try {
      const nextDark = !isDarkMode;
      setIsDarkMode(nextDark);
      if (nextDark) {
        document.documentElement.classList.add('dark');
        localStorage.setItem('theme_preference', 'dark');
      } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('theme_preference', 'light');
      }
      window.dispatchEvent(new CustomEvent('theme-preference-updated', { detail: nextDark ? 'dark' : 'light' }));
    } catch (e) {
      console.warn('Error toggling theme from sidebar:', e);
    }
  };

  const hasPermission = (permission: AdminPermission): boolean => {
    if (!user?.permissions) return false;
    if (user.permissions.includes('*')) return true;
    return user.permissions.includes(permission);
  };

  useEffect(() => {
    const handleConfigUpdate = () => setConfig(SponsoredService.getConfig());
    const handleThemeUpdate = (e: any) => {
      if (e.detail) {
        setIsDarkMode(e.detail === 'dark');
      } else {
        setIsDarkMode(document.documentElement.classList.contains('dark'));
      }
    };
    window.addEventListener('sponsored-config-updated', handleConfigUpdate);
    window.addEventListener('theme-preference-updated', handleThemeUpdate);
    return () => {
      window.removeEventListener('sponsored-config-updated', handleConfigUpdate);
      window.removeEventListener('theme-preference-updated', handleThemeUpdate);
    };
  }, []);

  const studentNavItems = [
    ...(activeCert?.modules?.dashboard !== false ? [{ id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' }] : []),
    { id: 'program', label: 'Program Guide', icon: Award, testId: 'nav-program' },
    ...(activeCert?.modules?.podcasts !== false && config.isPodcastsEnabled ? [{ id: 'podcasts', label: 'Audio Lessons', icon: PodcastIcon, testId: 'nav-podcasts' }] : []),
    ...(activeCert?.modules?.studyGuide !== false ? [{ id: 'guide', label: 'Study Guide', icon: BookCheck, testId: 'nav-guide' }] : []),
    ...(activeCert?.modules?.flashcards !== false ? [{ id: 'flashcards', label: 'Flashcards', icon: Zap, testId: 'nav-flashcards' }] : []),
    ...(activeCert?.modules?.practiceQuiz !== false ? [{ id: 'practice', label: 'Practice Quiz', icon: BookOpen, testId: 'nav-study' }] : []),
    ...(activeCert?.modules?.practiceExam !== false ? [{ id: 'exam', label: 'Practice Exam', icon: GraduationCap, testId: 'nav-exam' }] : []),
    ...(activeCert?.modules?.scenarios !== false ? [{ id: 'scenario', label: 'Scenario Challenges', icon: Stethoscope, testId: 'nav-scenario' }] : []),
    ...(activeCert?.modules?.skillsLab !== false ? [{ id: 'skills', label: 'Skill Lab', icon: Layers, testId: 'nav-skills' }] : []),
    ...(activeCert?.modules?.mathLab !== false ? [{ id: 'simulator', label: 'Math Lab', icon: Beaker, testId: 'nav-simulator' }] : []),
    ...(activeCert?.modules?.history !== false ? [{ id: 'history', label: 'Exam History', icon: History, testId: 'nav-history' }] : []),
    ...(config.isAnalyticsEnabled ? [{ id: 'analytics', label: 'Analytics Pro', icon: BarChart3, testId: 'nav-analytics' }] : []),
    { id: 'search', label: 'Study Hub Search', icon: Search, testId: 'nav-search' },
    ...(activeCert?.modules?.missedBank !== false ? [{ id: 'missed', label: 'Missed Bank', icon: Archive, testId: 'nav-missed' }] : []),
    ...(activeCert?.modules?.aiTutor !== false ? [{ id: 'tutor', label: 'Clinical Coach', icon: HeartPulse, testId: 'nav-tutor' }] : []),
    { id: 'faculty', label: 'Board Faculty', icon: Users, testId: 'nav-faculty' }
  ];

  const adminNavItems = [
    { id: 'admin-dashboard', label: 'Overview', icon: BarChart3, permission: 'admin.dashboard' as AdminPermission },
    { id: 'admin-analytics', label: 'Suite Analytics', icon: LineChart, permission: 'admin.dashboard' as AdminPermission },
    { id: 'admin-journey-auditor', label: 'Journey Auditor', icon: Compass, permission: 'admin.dashboard' as AdminPermission },
    { id: 'admin-cert-auditor', label: 'Cert Auditor', icon: ShieldCheck, permission: 'admin.dashboard' as AdminPermission },
    { id: 'admin-faculty', label: 'Faculty Manager', icon: Users, permission: 'admin.dashboard' as AdminPermission },
    { id: 'admin-certifications', label: 'Cert Manager', icon: Award, permission: 'admin.certifications' as AdminPermission },
    { id: 'admin-study-content', label: 'Study Content', icon: BookCheck, permission: 'admin.study-content' as AdminPermission },
    { id: 'admin-flashcards', label: 'Flashcard Studio', icon: Zap, permission: 'admin.flashcards' as AdminPermission },
    { id: 'admin-skills-content', label: 'Skills Content', icon: Layers, permission: 'admin.skills-content' as AdminPermission },
    { id: 'admin-clinical-simulators', label: 'Clinical Sim Studio', icon: Activity, permission: 'admin.skills-content' as AdminPermission },
    { id: 'admin-scenarios', label: 'Scenario Builder', icon: Stethoscope, permission: 'admin.scenarios' as AdminPermission },
    { id: 'admin-simulator', label: 'Simulator Lab', icon: Beaker, permission: 'admin.simulator-lab' as AdminPermission },
    { id: 'admin-podcasts', label: 'Podcast Library', icon: Mic2, permission: 'admin.podcasts' as AdminPermission },
    { id: 'admin-notifications', label: 'Broadcasts', icon: Bell, permission: 'admin.dashboard' as AdminPermission },
    { id: 'admin-feedback', label: 'Feedback Intel', icon: MessageSquare, permission: 'admin.feedback' as AdminPermission },
    { id: 'admin-factory', label: 'AI Factory', icon: Sparkles, permission: 'admin.factory' as AdminPermission },
    { id: 'admin-prompts', label: 'Prompt Lab', icon: Terminal, permission: 'admin.prompts' as AdminPermission },
    { id: 'admin-questions', label: 'Question Bank', icon: FileQuestion, permission: 'admin.questions' as AdminPermission },
    { id: 'admin-sponsored', label: 'System Control', icon: Megaphone, permission: 'admin.sponsored' as AdminPermission },
    { id: 'admin-promos', label: 'Promo Manager', icon: Tag, permission: 'admin.promos' as AdminPermission },
    { id: 'admin-users', label: 'Staff & Roles', icon: Users, permission: 'admin.users' as AdminPermission },
    { id: 'admin-config', label: 'Exam Config', icon: Settings, permission: 'admin.config' as AdminPermission },
    { id: 'admin-database', label: 'Registry Intel', icon: Database, permission: 'admin.database' as AdminPermission },
    { id: 'admin-seo', label: 'SEO Manager', icon: Globe, permission: 'admin.config' as AdminPermission },
    { id: 'admin-faqs', label: 'FAQ Board', icon: HelpCircle, permission: 'admin.config' as AdminPermission },
    { id: 'admin-ai-status', label: 'AI Connect', icon: Activity, permission: 'admin.ai-status' as AdminPermission }
  ];

  const navItems = (isAdmin && adminMode) ? adminNavItems.filter(item => hasPermission(item.permission)) : studentNavItems;

  const tokens = useThemeTokens();
  const isClinicalTheme = tokens.isClinical;

  return (
    <>
      <div 
        className={`fixed inset-0 bg-slate-900/80 z-[100] transition-opacity duration-300 lg:hidden ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} 
        onClick={onClose}
        aria-hidden="true"
      />
      <div 
        className={`fixed left-0 inset-y-0 z-[110] w-72 ${
          tokens.isWarmAcademic ? 'bg-[#0D6E6E] border-r border-[#0B5C5C]' :
          tokens.isClinical ? 'bg-[#0B2942]' :
          tokens.isHighContrast ? 'bg-black border-r-2 border-black text-amber-400' :
          tokens.isMinimal ? 'bg-slate-900 border-r border-slate-800' :
          tokens.isVibrant ? 'bg-gradient-to-b from-violet-950 via-indigo-950 to-slate-950' :
          'bg-slate-950'
        } text-white flex flex-col transition-transform duration-500 ease-in-out lg:w-64 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
        role={isOpen ? "dialog" : "complementary"}
        aria-modal={isOpen ? "true" : undefined}
        aria-label="Sidebar Navigation"
      >
        {/* Top Header inside Sidebar: On Desktop, displays logo on top of sidebar; on mobile displays logo + close button in drawer */}
        <div className={`h-16 md:h-20 px-4 md:px-5 border-b ${
          tokens.isWarmAcademic ? 'border-[#0B5C5C] bg-[#0B5C5C]' :
          tokens.isClinical ? 'border-slate-800 bg-[#092237]' :
          tokens.isHighContrast ? 'border-b-2 border-black bg-black' :
          tokens.isVibrant ? 'border-violet-900/50 bg-violet-950/40' :
          'border-slate-900 bg-slate-900/50'
        } flex justify-between items-center shrink-0`}>
          <a 
            href="/gateway" 
            onClick={() => { onClose(); }}
            className="flex items-center gap-2 hover:opacity-90 transition-opacity shrink-0"
            aria-label="Diamond School of Nursing Study Hub"
          >
            {(config.schoolLogoUrl || 'https://static.wixstatic.com/shapes/cc4206_330c2df5c9be45088f9d7ba5b00d2a6d.svg') ? (
              <img 
                src={config.schoolLogoUrl || 'https://static.wixstatic.com/shapes/cc4206_330c2df5c9be45088f9d7ba5b00d2a6d.svg'} 
                alt="Diamond School of Nursing" 
                className="h-7 sm:h-8 md:h-9 w-auto object-contain max-w-[150px] sm:max-w-[200px] bg-transparent shrink-0"
              />
            ) : (
              <h1 className="text-xl font-black flex items-center gap-2">
                <Zap aria-hidden="true" className="text-amber-400" size={24} />
                <span className="tracking-tight text-sm font-bold">{config.appName}</span>
              </h1>
            )}
          </a>
          <button 
            onClick={onClose} 
            className="p-2 text-slate-400 hover:text-white bg-white/5 rounded-xl transition-all active:scale-90 lg:hidden"
            aria-label="Close Sidebar"
          >
            <X size={22} />
          </button>
        </div>
        <nav className="flex-1 p-3 md:p-4 space-y-1 overflow-y-auto custom-scrollbar" aria-label="Main Navigation">
          {navItems.map((item) => (
            <button
              key={item.id}
              id={(item as any).testId}
              onClick={() => { onNavigate(item.id); onClose(); }}
              className={`w-full flex items-center gap-4 px-4 py-3.5 md:py-4 rounded-2xl transition-all group ${
                currentPage === item.id 
                  ? (tokens.isWarmAcademic ? 'bg-[#E05638] text-white shadow-lg translate-x-1 font-bold' :
                     tokens.isClinical ? 'bg-[#0F7D6C] text-white shadow-xl translate-x-1 font-bold' :
                     tokens.isHighContrast ? 'bg-amber-400 text-black border-2 border-black font-black translate-x-1' :
                     tokens.isMinimal ? 'bg-slate-800 text-white font-bold translate-x-1' :
                     tokens.isVibrant ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-xl translate-x-1 font-bold' :
                     'bg-medical-600 text-white shadow-xl translate-x-1') 
                  : 'text-slate-200 hover:bg-white/10 hover:text-white'
              }`}
              aria-current={currentPage === item.id ? 'page' : undefined}
            >
              <item.icon aria-hidden="true" size={20} />
              <span className="font-bold text-[13px] tracking-wide flex-1 text-left">{item.label}</span>
            </button>
          ))}
          <div className="mt-6 opacity-90 pb-10">
            <AdUnit placement="sidebar" />
          </div>
        </nav>
        
        <div className={`p-4 border-t shrink-0 ${
          tokens.isWarmAcademic ? 'bg-[#0B5C5C] border-[#0A5252]' :
          tokens.isClinical ? 'bg-[#092237] border-slate-800' :
          tokens.isHighContrast ? 'bg-black border-t-2 border-black' :
          'bg-slate-900/50 border-slate-900'
        }`}>
          <div className="px-3.5 py-3.5 bg-white/5 rounded-2xl flex items-center justify-between gap-3 border border-white/5">
             <div className="flex items-center gap-3 min-w-0">
               <div className={`w-9 h-9 rounded-xl shrink-0 ${isElite ? 'bg-gradient-to-br from-amber-400 via-yellow-200 to-amber-600' : tokens.iconBg} flex items-center justify-center font-black text-white shadow-lg`}>
                  {user ? (user.name?.charAt(0).toUpperCase() || 'S') : 'G'}
               </div>
               <div className="min-w-0">
                  <p className="text-[9px] font-black text-slate-400 tracking-widest uppercase">{user ? 'Active Path' : 'Guest Trial'}</p>
                  <p className="text-xs font-black text-white truncate tracking-tight">{activeCert?.shortName || 'Study Hub'}</p>
               </div>
             </div>

             {/* Sidebar Theme Switch */}
             <button
               onClick={toggleTheme}
               id="sidebar-theme-toggle-btn"
               className={`min-w-[36px] min-h-[36px] w-9 h-9 rounded-xl flex items-center justify-center transition-all active:scale-90 border shrink-0 cursor-pointer shadow-sm ${
                 !isDarkMode 
                   ? 'bg-amber-400/20 hover:bg-amber-400/30 border-amber-400/50 text-amber-300 ring-1 ring-amber-400/40' 
                   : 'bg-indigo-900/40 hover:bg-indigo-900/60 border-indigo-600/50 text-indigo-300 ring-1 ring-indigo-500/40'
               }`}
               title={!isDarkMode ? "Light Mode Active (Click for Dark Mode)" : "Dark Mode Active (Click for Light Mode)"}
               aria-label={!isDarkMode ? "Switch to Dark Mode" : "Switch to Light Mode"}
             >
               {!isDarkMode ? (
                 <Sun size={17} className="text-amber-300 fill-amber-300/40" />
               ) : (
                 <Moon size={17} className="text-indigo-300 fill-indigo-300/30" />
               )}
             </button>
          </div>
        </div>
      </div>
    </>
  );
};
